import { Code2, Shield, Rocket, CheckCircle, Activity, ArrowRight, Lock, Cpu } from 'lucide-react';
import styles from './HomePage.module.css';

const APP_URL = import.meta.env.VITE_APP_URL || 'https://app.console.rivellum.network';

export function HomePage() {
  const buildCards = [
    {
      icon: <Code2 size={40} />,
      title: 'Write & Edit',
      description: 'A full-featured code editor with syntax highlighting, project workspaces, and file management — right in your browser.'
    },
    {
      icon: <Rocket size={40} />,
      title: 'Deploy & Manage',
      description: 'Prepare contracts and project settings for deployment. On-chain actions are unavailable until a Rivellum network is running.'
    },
    {
      icon: <Lock size={40} />,
      title: 'Encrypted Ordering',
      description: 'Every transaction goes through encrypted batch admission and post-admission ordering — no front-running, no MEV.'
    },
    {
      icon: <Cpu size={40} />,
      title: 'Quantum-Ready',
      description: 'Secured by post-quantum cryptographic signing with the Natos wallet — hybrid classical + Dilithium3 authentication.'
    }
  ];

  const howItWorks = [
    {
      number: '1',
      title: 'Launch the Console',
      description: 'Open the Rivellum Console and sign in with your Natos wallet to get started.'
    },
    {
      number: '2',
      title: 'Create or Import',
      description: 'Start a new project from a prefab template, import files from your machine, or create a blank workspace.'
    },
    {
      number: '3',
      title: 'Build and Prepare',
      description: 'Write and validate code in the Console. Deployment and on-chain interactions are unavailable because no Rivellum network is currently running.'
    }
  ];

  const handleLaunchConsole = () => {
    window.open(APP_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className={styles.homePage}>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>
            Build and prepare
            <br />
            <span className={styles.heroTitleAccent}>Rivellum projects.</span>
          </h1>
          <p className={styles.heroSubtitle}>
            Rivellum Console is your developer workspace for writing and preparing code. No Rivellum network is currently running, so deployment and on-chain interactions are unavailable.
          </p>
          <div className={styles.heroActions}>
            <button onClick={handleLaunchConsole} className={styles.ctaPrimary}>
              Launch Console
              <ArrowRight size={20} />
            </button>
            <a
              href="https://portal.rivellum.network/docs"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaSecondary}
            >
              View Documentation
            </a>
          </div>
        </div>
      </section>

      {/* What You Can Build */}
      <section className={styles.whatYouCanBuild}>
        <h2 className={styles.sectionTitle}>Everything You Need</h2>
        <div className={styles.buildGrid}>
          {buildCards.map((card, index) => (
            <button
              key={index}
              className={styles.buildCard}
              style={{ animationDelay: `${index * 100}ms` }}
              onClick={handleLaunchConsole}
            >
              <div className={styles.buildIcon}>{card.icon}</div>
              <h3 className={styles.buildTitle}>{card.title}</h3>
              <p className={styles.buildDescription}>{card.description}</p>
              <div className={styles.buildArrow}>
                <ArrowRight size={20} />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className={styles.howItWorks}>
        <h2 className={styles.sectionTitle}>How It Works</h2>
        <div className={styles.stepsGrid}>
          {howItWorks.map((step, index) => (
            <div
              key={index}
              className={styles.step}
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className={styles.stepNumber}>{step.number}</div>
              <h3 className={styles.stepTitle}>{step.title}</h3>
              <p className={styles.stepDescription}>{step.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Status & Trust */}
      <section className={styles.statusTrust}>
        <h2 className={styles.sectionTitle}>Network Status</h2>
        <div className={styles.statusGrid}>
          <div className={styles.statusCard}>
            <div className={styles.statusIcon}>
              <Activity size={28} />
            </div>
            <div className={styles.statusInfo}>
              <div className={styles.statusLabel}>Network Status</div>
              <div className={styles.statusValue}>Operational</div>
            </div>
          </div>
          <div className={styles.statusCard}>
            <div className={styles.statusIcon}>
              <Rocket size={28} />
            </div>
            <div className={styles.statusInfo}>
              <div className={styles.statusLabel}>Latest Deployment</div>
              <div className={styles.statusValue}>v1.2.0</div>
            </div>
          </div>
          <div className={styles.statusCard}>
            <div className={styles.statusIcon}>
              <CheckCircle size={28} />
            </div>
            <div className={styles.statusInfo}>
              <div className={styles.statusLabel}>Security Model</div>
              <div className={styles.statusValue}>Post-Quantum</div>
            </div>
          </div>
        </div>
        <div className={styles.trustBanner}>
          <Shield size={20} />
          <span>Encrypted batch admission. Post-admission ordering. Post-quantum signing. No front-running.</span>
        </div>
      </section>
    </div>
  );
}
